import type { RefObject } from 'react';
import { merge } from 'lodash-es';
import React from 'react';

import type { AlertsProp } from '../../components/Alert/Alert';
import type { ShellPropSize, ShellPropType } from './utils';
import { atContainers, containers } from '../../common/containers';
import { devices } from '../../common/devices';
import { sizing } from '../../common/sizing';
import { Alerts } from '../../components/Alert/Alert';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { widthCSS } from '../../utilities/shared/Width';
import {
  shellControlGroupSize,
  shellVars,
  useShellMaxWidth,
  useShellMinWidth,
  useShellSize,
  useShellType,
  useShellWidth,
  useShellWidthMode,
} from './utils';

const ShellFooterAlerts = styled(Alerts, {
  widthAll: '100%',
});
ShellFooterAlerts.displayName = 'ShellFooterAlerts';

const ShellFooterEnds = styled(ControlGroup, {
  width: '100%',
});
ShellFooterEnds.displayName = 'ShellFooterEnds';

const ShellFooterStart = styled(ShellFooterEnds);
ShellFooterStart.displayName = 'ShellFooterStart';

const ShellFooterEnd = styled(ShellFooterEnds, {
  justifyContent: 'flex-end',
});
ShellFooterEnd.displayName = 'ShellFooterEnd';

const shellFooterSidesColumn = {
  flexDirection: 'column',
  justifyContent: 'center',
};

const shellFooterSidesRow = {
  flexDirection: 'row',
  justifyContent: 'space-between',
};

const ShellFooterSides = styled('div', {
  display: 'flex',
  alignItems: 'center',
  width: '100%',

  [containers.pane.narrow]: { ...shellFooterSidesColumn },

  ...atContainers([containers.pane.notNarrow, containers.drawer.all], { ...shellFooterSidesRow }),
});
ShellFooterSides.displayName = 'ShellFooterSides';

export const ShellFooterWidth = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  flexShrink: 0,
  gap: '$12',
  width: '100%',
  overflow: 'auto',
  background: colors.bgApplicationLight,

  [darkThemeSelector]: {
    background: colors.bgApplicationDark,
  },

  '&:before': {
    content: '',
    display: 'block',
    position: 'absolute',
    top: '-0.5px',
    height: '$1',
    background: colors.strokeNeutralLight,
    borderRadius: '99em',

    [darkThemeSelector]: {
      background: colors.strokeNeutralDark,
    },
  },

  variants: {
    size: {
      'x-small': {
        padding: `$6 ${sizing.contentSides}`,

        '&:before': {
          right: sizing.contentSides,
          left: sizing.contentSides,
        },

        [devices.touch]: {
          minHeight: shellVars.height['x-small'].touch,
        },

        [devices.mouse]: {
          minHeight: shellVars.height['x-small'].mouse,
        },
      },
      small: {
        padding: `$8 ${sizing.contentSides}`,

        '&:before': {
          right: sizing.contentSides,
          left: sizing.contentSides,
        },

        [devices.touch]: {
          minHeight: shellVars.height.small.touch,
        },

        [devices.mouse]: {
          minHeight: shellVars.height.small.mouse,
        },
      },
      medium: {
        padding: `$10 ${sizing.sides}`,

        '&:before': {
          right: sizing.sides,
          left: sizing.sides,
        },

        [devices.touch]: {
          minHeight: shellVars.height.medium.touch,
        },

        [devices.mouse]: {
          minHeight: shellVars.height.medium.mouse,
        },
      },
    },
  },
});
ShellFooterWidth.displayName = 'ShellFooterWidth';

const ShellFooterContainer = styled('div', {
  gridArea: 'shell-footer',
  position: 'relative',
  display: 'flex',

  '@mobile': {
    flexDirection: 'row',
  },

  '@notMobile': {
    flexDirection: 'column',
  },

  variants: {
    widthMode: {
      centered: {
        alignItems: 'center',
      },
      full: {},
    },
  },
});
ShellFooterContainer.displayName = 'ShellFooterContainer';

export type ShellFooterProps = {
  /**
   * The alert content for the footer, usually used to display a warning or error.
   */
  alerts?: AlertsProp;
  /**
   * The end content for the footer, usually used for actions.
   */
  end?: React.ReactNode;
  /**
   * Optional ref to use for the footer.
   */
  ref?: RefObject<HTMLDivElement | null>;
  /**
   * The start content for the footer, usually used for additional context and helpers.
   */
  start?: React.ReactNode;
  /**
   * Provide a size for your shell.
   * Note: Automatically provided from the `Shell` component, you likely don't need to provide this.
   */
  size?: ShellPropSize;
  /**
   * Shell frame type for container queries (`container-name` / `containers.*` in atto).
   * Inherits from `Shell` via context when omitted. Pane/Drawer wrappers set this explicitly.
   */
  type?: ShellPropType;
  /** Children for the component. */
  children?: React.ReactNode;
};

export function ShellFooter({
  alerts,
  end,
  size,
  start,
  ref,
  type,
  children,
  ...remaining
}: ShellFooterProps) {
  const shellSize = useShellSize(size, 'medium');
  const controlGroupSize = shellControlGroupSize(shellSize);
  const shellType = useShellType(type);
  const shellWidth = useShellWidth();
  const shellMaxWidth = useShellMaxWidth();
  const shellMinWidth = useShellMinWidth();
  const shellWidthMode = useShellWidthMode();

  return (
    <ShellFooterContainer ref={ref} widthMode={shellWidthMode} {...remaining}>
      <ShellFooterWidth
        size={shellSize}
        css={merge(
          {
            containerType: 'inline-size',
            containerName: shellType,
          },
          shellWidth ? widthCSS(shellWidth) : undefined,
          shellMaxWidth ? maxWidthCSS(shellMaxWidth) : undefined,
          shellMinWidth ? minWidthCSS(shellMinWidth) : undefined,
        )}
      >
        {children}
        {alerts && (
          <ShellFooterAlerts
            type="inline"
            relation="standalone"
            alerts={alerts.map((alert) => ({ ...alert }))}
          />
        )}
        <ShellFooterSides>
          {start && (
            <ShellFooterStart relation="separate" size={controlGroupSize}>
              {start}
            </ShellFooterStart>
          )}
          {end && (
            <ShellFooterEnd relation="separate" size={controlGroupSize}>
              {end}
            </ShellFooterEnd>
          )}
        </ShellFooterSides>
      </ShellFooterWidth>
    </ShellFooterContainer>
  );
}
